import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { fetchAttributes } from 'src/store/slices/attributesSlice';
import {
  fetchCostCenters,
  fetchCountries,
  fetchFormFieldMetadata,
  fetchFpnProductTypes,
} from 'src/store/slices/commonSlice';
import { fetchMandatoryAttributes } from 'src/store/slices/productHierarchySlice';
import { fetchScenarioSnapshots } from 'src/store/slices/scenarioSnapshotsSlice';
import { fetchUserFavorites } from 'src/store/slices/userPreferencesSlice';
import { logger } from 'src/utils/logger';

/**
 * Hook to prefetch expensive data at application startup.
 *
 * This hook triggers API calls in a "fire-and-forget" manner - it dispatches
 * the actions without waiting for them to complete. This allows the UI to
 * render immediately while data loads in the background.
 *
 * Data prefetched:
 * - Product lines (lightweight list used by side navigation Select dropdown)
 * - Product hierarchy (used by LastEditedProgramsWidget and ManagePrograms page)
 * - Form field metadata (common lookup data for form dropdowns across the app)
 * - Cost centers (cost center code and name for cost center dropdowns)
 * - Countries (country list for filter dropdowns)
 * - Scenario snapshots (scenario list for filter dropdowns)
 * - Attributes (attribute definitions for RPN/FPN attribute dropdowns)
 * - Mandatory attributes (validation rules for required attributes by product line)
 * - User favorites (favorite Product Lines, RPNs, and FPNs for the current user)
 *
 * The hook respects existing cache logic in Redux slices, so it won't
 * refetch data that's already been fetched or is currently being fetched.
 */
export const usePrefetchData = (): void => {
  const dispatch = useAppDispatch();
  const hasPrefetchedRef = useRef(false);

  // Check if form field metadata has already been fetched or is loading
  const isFormFieldMetadataLoaded = useAppSelector((state) => state.common.isFormFieldMetadataLoaded);
  const isFormFieldMetadataLoading = useAppSelector((state) => state.common.loading.formFieldMetadata);

  // Check if cost centers have already been fetched or is loading
  const isCostCentersLoaded = useAppSelector((state) => state.common.isCostCentersLoaded);
  const isCostCentersLoading = useAppSelector((state) => state.common.loading.costCenters);

  // Check if countries have already been fetched or is loading
  const isCountriesLoaded = useAppSelector((state) => state.common.isCountriesLoaded);
  const isCountriesLoading = useAppSelector((state) => state.common.loading.countries);

  // Check if FPN product types have already been fetched or is loading
  const hasAttemptedFpnProductTypesFetch = useAppSelector(
    (state) => state.common.hasAttemptedFpnProductTypesFetch
  );
  const isFpnProductTypesLoading = useAppSelector((state) => state.common.loading.fpnProductTypes);

  // Check if scenario snapshots have already been fetched or is loading
  const hasAttemptedScenariosFetch = useAppSelector(
    (state) => state.scenarioSnapshots.scenarioSnapshots.hasAttemptedFetch
  );
  const isScenariosLoading = useAppSelector((state) => state.scenarioSnapshots.scenarioSnapshots.isLoading);

  // Check if attributes has already been fetched or is loading
  const hasAttemptedAttributesFetch = useAppSelector((state) => state.attributes.hasAttemptedFetch);
  const isAttributesLoading = useAppSelector((state) => state.attributes.isLoading);

  // Check if mandatory attributes have already been fetched or is loading
  const hasAttemptedMandatoryAttributesFetch = useAppSelector(
    (state) => state.productHierarchy.mandatoryAttributes.hasAttemptedFetch
  );
  const isMandatoryAttributesLoading = useAppSelector((state) => state.productHierarchy.mandatoryAttributes.isLoading);

  // Check if user favorites have already been fetched or is loading
  const hasAttemptedFavoritesFetch = useAppSelector((state) => state.userPreferences.hasAttemptedFavoritesFetch);
  const isFavoritesLoading = useAppSelector((state) => state.userPreferences.isLoadingFavorites);

  useEffect(() => {
    // Only prefetch once per app session
    if (hasPrefetchedRef.current) {
      return;
    }

    // Prefetch form field metadata (common lookup data for form dropdowns)
    if (!isFormFieldMetadataLoaded && !isFormFieldMetadataLoading) {
      logger.info('usePrefetchData: Triggering prefetch for form field metadata');
      dispatch(fetchFormFieldMetadata());
    }

    // Prefetch cost centers (cost center code and name for cost center dropdowns)
    if (!isCostCentersLoaded && !isCostCentersLoading) {
      logger.info('usePrefetchData: Triggering prefetch for cost centers');
      dispatch(fetchCostCenters());
    }

    // Prefetch countries (country list for filter dropdowns)
    if (!isCountriesLoaded && !isCountriesLoading) {
      logger.info('usePrefetchData: Triggering prefetch for countries');
      dispatch(fetchCountries());
    }

    // Prefetch FPN product types (DEVICE/ACCESSORY classification for the Compatible Devices
    // dropdown filter). Single global call; fails open to empty if the backend query isn't ready.
    if (!hasAttemptedFpnProductTypesFetch && !isFpnProductTypesLoading) {
      logger.info('usePrefetchData: Triggering prefetch for FPN product types');
      dispatch(fetchFpnProductTypes());
    }

    // Prefetch scenario snapshots (scenario list for filter dropdowns)
    if (!hasAttemptedScenariosFetch && !isScenariosLoading) {
      logger.info('usePrefetchData: Triggering prefetch for scenario snapshots');
      dispatch(fetchScenarioSnapshots({}));
    }

    // Prefetch attributes (attribute definitions for RPN/FPN attribute dropdowns)
    if (!hasAttemptedAttributesFetch && !isAttributesLoading) {
      logger.info('usePrefetchData: Triggering prefetch for attributes');
      dispatch(fetchAttributes(false));
    }

    // Prefetch mandatory attributes (validation rules for required attributes)
    if (!hasAttemptedMandatoryAttributesFetch && !isMandatoryAttributesLoading) {
      logger.info('usePrefetchData: Triggering prefetch for mandatory attributes');
      dispatch(fetchMandatoryAttributes(false));
    }

    // Prefetch user favorites (favorite Product Lines, RPNs, and FPNs)
    if (!hasAttemptedFavoritesFetch && !isFavoritesLoading) {
      logger.info('usePrefetchData: Triggering prefetch for user favorites');
      dispatch(fetchUserFavorites());
    }

    hasPrefetchedRef.current = true;
  }, [
    dispatch,
    isFormFieldMetadataLoaded,
    isFormFieldMetadataLoading,
    isCostCentersLoaded,
    isCostCentersLoading,
    isCountriesLoaded,
    isCountriesLoading,
    hasAttemptedScenariosFetch,
    isScenariosLoading,
    hasAttemptedAttributesFetch,
    isAttributesLoading,
    hasAttemptedMandatoryAttributesFetch,
    isMandatoryAttributesLoading,
    hasAttemptedFavoritesFetch,
    isFavoritesLoading,
  ]);
};
